import {
  Button,
  Dialog,
  DialogContent,
  DialogActions,
  TextField,
  Divider,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Typography,
  Autocomplete,
  CircularProgress,
} from "@mui/material";
import { Stack } from "@mui/system";
import React, { useState, useRef, useEffect } from "react";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import FileUploadIcon from "../../assets/images/FileUpload_Icon.svg";
import { jwtDecode } from "jwt-decode";
import { useAuth } from "../../context/AuthProvider";
import { getProject } from "../../services/project.service";
import {
  uploadSupportingDocument,
  getAllSupportingDocuments,
  deleteSupportingDocument,
  updateSupportingDocument,
  getSupportingDocById,
} from "../../services/client.service";
import dayjs from "dayjs";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Formik, Form, Field } from "formik";

const SupportingFileUpload = () => {
  const [open, setOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [fileName, setFileName] = useState("");
  const [currentFileId, setCurrentFileId] = useState(null);
  const [fileDescription, setFileDescription] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [projectFilter, setProjectFilter] = useState("All");
  const [projectData, setProjectData] = useState([]);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState({
    projects: false,
    documents: false,
    upload: false,
    delete: false,
    update: false,
    currentAction: null,
  });
  const { accessToken } = useAuth();
  const fileInputRef = useRef(null);

  const getUserIdFromToken = () => {
    if (!accessToken) return null;
    try {
      const decoded = jwtDecode(accessToken);
      return decoded.id;
    } catch (error) {
      console.error("Invalid token", error);
      return null;
    }
  };

  const clientId = getUserIdFromToken();

  const GetProjectData = async () => {
    try {
      setLoading((prev) => ({ ...prev, projects: true }));
      const res = await getProject({
        clientId: clientId,
        isEnabled: true,
      });
      if (res.data.data && Array.isArray(res.data.data)) {
        setProjectData(res.data.data.map((e) => ({ ...e, label: e.name })));
      }
    } catch (error) {
      console.error("Error fetching project data:", error);
    } finally {
      setLoading((prev) => ({ ...prev, projects: false }));
    }
  };

  const fetchSupportingDocuments = async () => {
    try {
      setLoading((prev) => ({ ...prev, documents: true }));
      const response = await getAllSupportingDocuments({
        projectId: projectFilter,
      });
      setUploadedFiles(response.data);
    } catch (error) {
      console.error("Error fetching documents:", error);
    } finally {
      setLoading((prev) => ({ ...prev, documents: false }));
    }
  };

  const handleProjectChange = (newValue) => {
    setSelectedProject(newValue?._id || null);
  };

  const handleProjectfilter = (newValue) => {
    setProjectFilter(newValue?._id);
  };

  const handleClickOpen = () => {
    setEditMode(false);
    setCurrentFileId(null);
    setFileDescription("");
    setSelectedFile(null);
    setSelectedProject(null);
    setOpen(true);
  };

  const handleClose = () => {
    if (!loading.currentAction) {
      setOpen(false);
      resetForm();
    }
  };

  const resetForm = () => {
    setFileDescription("");
    setSelectedFile(null);
    setSelectedProject(null);
    setEditMode(false);
    setCurrentFileId(null);
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    setSelectedFile(file);
  };

  const handleUpload = async () => {
    try {
      setLoading((prev) => ({
        ...prev,
        upload: true,
        currentAction: "upload",
      }));
      if (!validateForm()) return;
      const formData = new FormData();
      formData.append("fileName", fileName);
      formData.append("file", selectedFile);
      formData.append("clientId", clientId);
      formData.append("projectId", selectedProject);
      formData.append("description", fileDescription);

      await uploadSupportingDocument(formData);
      await fetchSupportingDocuments();
      resetForm();
      setOpen(false);
    } catch (error) {
      console.error("Upload failed:", error);
    } finally {
      setLoading((prev) => ({ ...prev, upload: false, currentAction: null }));
    }
  };

  const handleDeleteFile = async (fileId) => {
    try {
      setLoading((prev) => ({
        ...prev,
        delete: true,
        currentAction: `delete-${fileId}`,
      }));
      await deleteSupportingDocument(fileId);
      await fetchSupportingDocuments();
    } catch (error) {
      console.error("Delete failed:", error);
    } finally {
      setLoading((prev) => ({ ...prev, delete: false, currentAction: null }));
    }
  };

  const handleEditClick = async (fileId) => {
    try {
      setLoading((prev) => ({
        ...prev,
        update: true,
        currentAction: `edit-${fileId}`,
      }));
      const response = await getSupportingDocById(fileId);
      const fileData = response.data;

      setEditMode(true);
      setCurrentFileId(fileId);
      setFileName(fileData.fileName || "");
      setFileDescription(fileData.description || "");
      setSelectedProject(fileData.projectId || null);
      setOpen(true);
    } catch (error) {
      console.error("Error fetching file data:", error);
    } finally {
      setLoading((prev) => ({ ...prev, update: false, currentAction: null }));
    }
  };

  const handleUpdate = async () => {
    if (!currentFileId) return;

    try {
      setLoading((prev) => ({
        ...prev,
        update: true,
        currentAction: "update",
      }));
      const formData = new FormData();
      if (selectedFile) {
        formData.append("file", selectedFile);
      }
      formData.append("fileName", fileName);
      formData.append("clientId", clientId);
      formData.append("projectId", selectedProject);
      formData.append("description", fileDescription);

      await updateSupportingDocument(currentFileId, formData);
      await fetchSupportingDocuments();
      resetForm();
      setOpen(false);
    } catch (error) {
      console.error("Update failed:", error);
    } finally {
      setLoading((prev) => ({ ...prev, update: false, currentAction: null }));
    }
  };

  const truncateText = (text, maxLength = 15) =>
    text
      ? text.length > maxLength
        ? `${text.substring(0, maxLength)}...`
        : text
      : "-";

  const formatDate = (dateString) => {
    return dateString ? dayjs(dateString).format("MMM D, YYYY h:mm A") : "-";
  };

  const handleDownload = (fileId) => {
    const { file } = uploadedFiles.find((file) => file._id === fileId);
    if (file?.location) {
      const link = document.createElement("a");
      link.href = file.location;
      link.download = "";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else {
      console.error("File not found or URL missing");
    }
  };

  useEffect(() => {
    GetProjectData();
    fetchSupportingDocuments();
  }, []);

  useEffect(() => {
    if (projectFilter) {
      fetchSupportingDocuments();
    }
  }, [projectFilter]);
  const validateForm = () => {
    const newErrors = {};

    if (!fileName.trim()) newErrors.fileName = "File name is required *";
    if (!selectedProject) newErrors.projectId = "Project is required *";
    if (!editMode && !selectedFile)
      newErrors.file = "File upload is required *";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  return (
    <Box sx={{ p: 3 }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        mb={2}
        flexWrap="wrap"
        gap={2}
      >
        <Typography sx={{ fontFamily: "Manrope", fontSize: "16px" }}>
          Supporting File Upload
        </Typography>
        <Stack direction="row" gap={2} alignItems="center" flexWrap="wrap">
          <Autocomplete
            options={[{ _id: "All", label: "All Projects" }, ...projectData]}
            getOptionLabel={(option) => option?.label || ""}
            value={
              projectFilter === "All"
                ? { _id: "All", label: "All Projects" }
                : projectData.find((proj) => proj?._id === projectFilter) ||
                  null
            }
            onChange={(_, newValue) => handleProjectfilter(newValue)}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Filter by Project"
                size="small"
                sx={{ minWidth: 200 }}
                disabled={loading.projects || !!loading.currentAction}
              />
            )}
            disabled={loading.projects || !!loading.currentAction}
            sx={{ minWidth: 200 }}
          />
          <Button
            onClick={handleClickOpen}
            variant="contained"
            sx={{
              backgroundColor: "#008080",
              color: "white",
              textTransform: "none",
              borderRadius: "5px",
              minHeight: "40px",
              minWidth: "140px",
              fontFamily: "Manrope",
              "&:hover": { backgroundColor: "#006666" },
            }}
            disabled={!!loading.currentAction}
          >
            Upload File
          </Button>
        </Stack>
      </Stack>

      {loading.documents ? (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box mt={3} sx={{ overflowX: "auto" }}>
          <Typography
            sx={{
              fontFamily: "Manrope",
              fontSize: "16px",
              mb: 2,
              fontWeight: 600,
            }}
          >
            Uploaded Files
          </Typography>
          <TableContainer
            component={Paper}
            sx={{ border: "1px solid #e0e0e0" }}
          >
            <Table sx={{ minWidth: 650 }} size="small">
              <TableHead sx={{ backgroundColor: "#f5f5f5", height:"50px" }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, width: "25%" }}>
                    File Name
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, width: "25%" }}>
                    Project
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, width: "30%" }}>
                    Description
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, width: "20%" }}>
                    Uploaded Date
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, width: "10%" }}>
                    Action
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody sx={{ height: uploadedFiles.length === 0 ? '64vh' : 'auto' }}>
                {uploadedFiles.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={5}
                      align="center"
                      sx={{
                        border: 0,
                        '&.MuiTableCell-root': {
                          borderBottom: 'none'
                        }
                      }}
                    >
                      No files uploaded
                    </TableCell>
                  </TableRow>
                ) : (
                  uploadedFiles.map((file) => (
                    <TableRow key={file._id} hover>
                      <TableCell>
                        <Box
                          sx={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                          title={file.file?.originalName || file.fileName}
                        >
                          {truncateText(
                            file.file?.originalName || file.fileName,
                            20
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box
                          sx={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                          title={file?.projectName}
                        >
                          {file?.projectName}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box
                          sx={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                          title={file.description}
                        >
                          {truncateText(file.description, 25)}
                        </Box>
                      </TableCell>

                      <TableCell>{formatDate(file.createdAt)}</TableCell>
                      <TableCell sx={{ display: "inline-flex", gap: 1 }}>
                        <IconButton
                          onClick={() => handleEditClick(file._id)}
                          color="primary"
                          size="small"
                          disabled={loading.currentAction === `edit-${file._id}`}
                        >
                          {loading.currentAction === `edit-${file._id}` ? (
                            <CircularProgress size={20} />
                          ) : (
                            <EditIcon fontSize="small" />
                          )}
                        </IconButton>
                        <IconButton
                          onClick={() => handleDownload(file._id)}
                          color="success"
                          size="small"
                        >
                          <FileDownloadIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          onClick={() => handleDeleteFile(file._id)}
                          color="error"
                          size="small"
                          disabled={
                            loading.currentAction === `delete-${file._id}`
                          }
                        >
                          {loading.currentAction === `delete-${file._id}` ? (
                            <CircularProgress size={20} />
                          ) : (
                            <DeleteIcon fontSize="small" />
                          )}
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        disableEscapeKeyDown={!!loading.currentAction}
      >
        <Box sx={{ p: 2 }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography
              sx={{ fontFamily: "Manrope", fontSize: "18px", fontWeight: 600 }}
            >
              {editMode ? "Edit Supporting File" : "Upload Supporting File"}
            </Typography>
            <IconButton
              onClick={handleClose}
              disabled={!!loading.currentAction}
              sx={{ cursor: loading.currentAction ? "not-allowed" : "pointer" }}
            >
              <CloseIcon />
            </IconButton>
          </Stack>
        </Box>
        <Divider />
        <DialogContent sx={{ p: 3 }}>
          <Stack spacing={3}>
            <Box>
              <Typography sx={{ fontFamily: "Manrope" }}>File Name</Typography>
              <TextField
                name="fileName"
                placeholder="File Name"
                variant="outlined"
                fullWidth
                value={fileName}
                onChange={(e) => {
                  setFileName(e.target.value);
                }}
                disabled={!!loading.currentAction}
                error={!!errors.fileName}
                helperText={errors.fileName}
              />
            </Box>
            {loading.projects ? (
              <Box sx={{ display: "flex", justifyContent: "center" }}>
                <CircularProgress />
              </Box>
            ) : (
              <Stack>
                <Typography sx={{ fontFamily: "Manrope" }}>Project</Typography>
                <Autocomplete
                  options={projectData}
                  getOptionLabel={(option) => option?.label || ""}
                  value={
                    projectData.find((proj) => proj?._id === selectedProject) ||
                    null
                  }
                  onChange={(_, newValue) => {
                    //setFieldValue("projectId", newValue?._id || "");
                    handleProjectChange(newValue);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Select a Project"
                      disabled={!!loading.currentAction}
                      error={!!errors.projectId}
                      helperText={errors.projectId}
                      sx={{
                        "& .MuiInputBase-root": {
                          minHeight: "36px", // reduces input height
                          paddingRight: "6px",
                        },
                        "& .MuiInputBase-input": {
                          padding: "0px 0px", // reduce top/bottom & side padding
                          fontSize: "0.9rem", // slightly smaller text
                        },
                      }}
                    />
                  )}
                  disabled={!!loading.currentAction}
                  isOptionEqualToValue={(option, value) =>
                    option._id === value._id
                  }
                />
              </Stack>
            )}

            <Box>
              <Typography sx={{ mb: 1, fontFamily: "Manrope" }}>
                Description
              </Typography>
              <TextField
                name="description"
                value={fileDescription}
                onChange={(e) => {
                  //handleChange(e);
                  setFileDescription(e.target.value);
                }}
                multiline
                fullWidth
                placeholder="Enter file description"
                size="small"
                sx={{
                  fontFamily: "Manrope",
                  "& .MuiInputBase-root textarea": {
                    overflow: "hidden",
                    resize: "none",
                    height: "auto",
                    textAlign: "left",
                  },
                  "& .MuiInputBase-root": {
                    minHeight: "100px !important",
                    height: "auto !important",
                  },
                }}
                inputProps={{ maxLength: 500 }}
                disabled={!!loading.currentAction}
              />
              <Typography variant="caption" sx={{ fontFamily: "Manrope" }}>
                {fileDescription.length}/500 characters
              </Typography>
            </Box>
            <Box>
              <Typography sx={{ mb: 1, fontFamily: "Manrope" }}>
                {editMode ? "Update File (Optional)" : "Upload File *"}
              </Typography>
              <Box
                onClick={() =>
                  !loading.currentAction && fileInputRef.current.click()
                }
                sx={{
                  border: "1px dashed #A5A5A5",
                  borderRadius: "4px",
                  p: 3,
                  textAlign: "center",
                  cursor: loading.currentAction ? "not-allowed" : "pointer",
                  "&:hover": {
                    borderColor: loading.currentAction ? "#A5A5A5" : "#008080",
                    backgroundColor: loading.currentAction
                      ? "inherit"
                      : "rgba(0, 128, 128, 0.04)",
                  },
                  opacity: loading.currentAction ? 0.7 : 1,
                }}
              >
                <Stack alignItems="center" spacing={1}>
                  <img
                    src={FileUploadIcon}
                    alt="FileUploadIcon"
                    style={{ height: "40px", width: "40px" }}
                  />
                  <Typography sx={{ fontFamily: "Manrope" }}>
                    {selectedFile ? selectedFile.name : "Click to upload file"}
                  </Typography>
                  <Typography variant="caption" sx={{ fontFamily: "Manrope" }}>
                    Max file size: 10MB
                  </Typography>
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileChange}
                    style={{ display: "none" }}
                    disabled={!!loading.currentAction}
                  />
                </Stack>
              </Box>
              {errors.file && (
                <Typography color="error" variant="caption">
                  {errors.file}
                </Typography>
              )}
            </Box>
          </Stack>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={handleClose}
            sx={{
              color: "black",
              textTransform: "none",
              border: "1px solid #A5A5A5",
              fontFamily: "Manrope",
              "&:hover": { border: "1px solid #A5A5A5" },
            }}
            disabled={!!loading.currentAction}
          >
            Cancel
          </Button>
          <Stack
            onClick={editMode ? handleUpdate : handleUpload}
            variant="contained"
            disabled={
              !!loading.currentAction ||
              (!editMode && (!selectedFile || !selectedProject))
            }
            sx={{
              background: "#008080",
              textTransform: "none",
              color: "#ffffff",
              fontFamily: "Manrope",
              minWidth: "100px",
              padding: "9px 5px",
              textAlign: "center",
              borderRadius: "6PX",
            }}
          >
            {loading.currentAction ? (
              <CircularProgress size={24} color="inherit" />
            ) : editMode ? (
              <Typography sx={{ textAlign: "center", cursor: "pointer" }}>
                {" "}
                Update
              </Typography>
            ) : (
              <Typography> Upload</Typography>
            )}
          </Stack>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SupportingFileUpload;
